import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import LoadingAnimation from '@zr/components/LoadingAnimation';
import withErrorContainer from '@zr/components/withErrorContainer';

import useMergeParsedResume from './useMergeParsedResume';
import useProfileEntryCorrectionWizard from './useProfileEntryCorrectionWizard';

import ParseEntryCorrectionIntro from './ParseEntryCorrectionIntro';

import {
  STRING_PARSE_WAIT,
  STRING_PARSE_SUBMIT,
  STRING_PARSE_SKIP,
} from './labels';

import './MergeParsedResume.less';

/**
 * MergeParsedResume links logic from useMergeParsedResume.js and useProfileEntryCorrectionWizard.js
 * This component manages what to show depending on the state of their parsed resume.
 * Generally, it just shows a Please Wait... loading animation unless there is parsed data that the user needs to fix.
 * In the latter case, the profile entry correction wizard is shown. Wizard logic is driven from the custom hook.
 * On any kind of finish result (error, success, timeout, etc.), the respective callback is invoked for the parent component to decide what to do.
 */
export function MergeParsedResume({
  companyName,
  onEntryInteraction,
  onEntryView,
  onError,
  onErrorClose,
  onInitializedWizard,
  onMergeFailure,
  onMergeSuccess,
  onPollLimitReached,
  profileMethod,
}) {
  // custom hook handling profile entry wizard logic
  // this is actually used if useMergedParsedResume.js signals there are incomplete entries
  const wizardObject = useProfileEntryCorrectionWizard({
    // fade half a second in between form completions
    // 500 ms matches the fade animation times in MergeParsedResume.less
    fadeTime: 500,
    // called with an entry telemetry object
    onEntryInteraction,
    // called with an entry telemetry object
    onEntryView,
    onError,
    // called when successfully initialized wizard data for display
    onInitializedWizard,
    // called when we tried to merge their fixed their changes, but ran into a problem we can't proceed through
    onMergeFailure,
    // called when we successfully merged and fixed their changes
    onMergeSuccess,
    // called at the start of each form submit
    onStartSubmit: onErrorClose,
    // profile_method for profile form submits
    profileMethod,
  });

  const {
    // display index for progress display
    entryDisplayIndex,
    // formik initialTouched
    entryInitialTouched,
    // formik initialValues, this changes after each entry to prefill the next incomplete entry
    entryInitialValues,
    // formik validationSchema, this changes depending on which entry form is used
    entryValidationSchema,
    // get FieldsetComponent depending on which entry form is used
    getFieldsetComponentByEntryKey,
    // if there are 'needs_fix' incomplete entries from useMergeParsedResume, then initialize this wizard
    initializeWizard,
    // toggle showing parse entry correction intro
    introDone,
    // run when finishing intro
    onIntroDone,
    // fade boolean that toggles fade animation
    isFading,
    // boolean flag that is true when initialization is done, only show the wizard when it is true
    isWizardInitialized,
    // skip entry handler
    skipEntry,
    // submit entry handler
    submitEntry,
    // display total amount of entries for progress display
    totalEntries,
  } = wizardObject;

  // custom hook handling initial mergedResume.js api requests
  // isWaiting will start off as true while these network requests happen
  // if onMergeNeedsFix happens, isWaiting will toggle false and we'll initialize profile entry correction wizard
  const { isWaiting } = useMergeParsedResume({
    // if an unexpected error happened for the first fetch, just abort
    // there's probably no good user interface for the jobseeker if that happens
    onError: onMergeFailure,
    // on an actual merge resume failure, we abort immediately
    onMergeFailure,
    // on needs_fix, start parse entry correction wizard
    onMergeNeedsFix: initializeWizard,
    // on success, we're done immediately
    onMergeSuccess,
    // on poll timeout
    onPollLimitReached,
    // profile_method for profile update
    profileMethod,
  });

  return (
    <div className="MergeParsedResume">
      {isWaiting && <LoadingAnimation loadingMessage={STRING_PARSE_WAIT} />}
      {isWizardInitialized && (
        <>
          {!introDone && (
            <ParseEntryCorrectionIntro
              companyName={companyName}
              onDone={onIntroDone}
              totalEntries={totalEntries}
            />
          )}
          {introDone && (
            /**
             * initialTouched/initialValues/validationSchema will update as the user advances through each of their incomplete entries
             * enableReinitialize/validateOnMount adjust formik to show errors immediately and update the form with new initial form props
             */
            <Formik
              enableReinitialize
              initialTouched={entryInitialTouched}
              initialValues={entryInitialValues}
              onSubmit={submitEntry}
              validateOnMount
              validationSchema={entryValidationSchema}
            >
              {(formikProps) => {
                const { handleSubmit, values } = formikProps;

                // we might switch entire fieldsets throughout this wizard (experience -> education)
                // we need to only render the FieldsetComponent when Formik values are ready for it
                // useProfileEntryCorrectionWizard.js also returns FieldsetComponent directly, but React won't like rendering that until Formik values are updated
                // the custom hook sets which fieldset component to use as a hidden input value fieldsetComponentKey
                const FieldsetComponent = getFieldsetComponentByEntryKey(values.fieldsetComponentKey);

                return (
                  <form
                    className={`merge_parsed_resume_form ${isFading ? 'fade_out' : ''}`}
                    onSubmit={handleSubmit}
                  >
                    <FieldsetComponent
                      disabled={isFading}
                      entryIndex={entryDisplayIndex}
                      formikProps={formikProps}
                      totalEntries={totalEntries}
                      enableRichText
                    />
                    <div className="button_wrapper">
                      <button
                        className="skip_button"
                        disabled={isFading}
                        onClick={skipEntry}
                        type="button"
                      >
                        {STRING_PARSE_SKIP}
                      </button>
                      <button
                        className="submit_button"
                        disabled={isFading}
                        type="submit"
                      >
                        {STRING_PARSE_SUBMIT}
                      </button>
                    </div>
                  </form>
                );
              }}
            </Formik>
          )}
        </>
      )}
    </div>
  );
}

MergeParsedResume.propTypes = {
  companyName: PropTypes.string,
  onEntryInteraction: PropTypes.func,
  onEntryView: PropTypes.func,
  onError: PropTypes.func.isRequired,
  onErrorClose: PropTypes.func.isRequired,
  onInitializedWizard: PropTypes.func,
  onMergeFailure: PropTypes.func.isRequired,
  onMergeSuccess: PropTypes.func.isRequired,
  onPollLimitReached: PropTypes.func.isRequired,
  profileMethod: PropTypes.string,
};

MergeParsedResume.defaultProps = {
  companyName: null,
  onEntryInteraction: () => {},
  onEntryView: () => {},
  onInitializedWizard: () => {},
  // this should be overridden
  profileMethod: 'fe.jobseeker-parse-resume.MergeParsedResume.validate_only',
};

export default withErrorContainer(MergeParsedResume);
